import { LoadingButton } from "@mui/lab";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  APIMonitorType,
  APIMonitoringEnrollmentMonitorConfiguration,
} from "../../../types/APIMonitoringEnrollment";
import { useTrueBizApi } from "../../../api";
import { useShowNetworkFailureToast } from "../../NetworkFailureToast";

export default function PauseResumeButton({
  domain,
  monitorType,
  isEnabled,
}: {
  domain: string;
  monitorType: APIMonitorType;
  isEnabled: boolean;
}) {
  const api = useTrueBizApi();
  const queryClient = useQueryClient();
  const showToast = useShowNetworkFailureToast();

  const configurationMutation = useMutation({
    mutationFn: async () =>
      api.setMonitorEnabled({
        domain,
        monitor_type: monitorType,
        is_enabled: !isEnabled,
      }),
    mutationKey: ["configureEnrollment", domain, monitorType, !isEnabled],
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["getMonitor", domain] });
      const previous = queryClient.getQueryData(["getMonitor", domain]);

      if (previous === undefined) return; // query hasn't been populated yet

      queryClient.setQueryData(["getMonitor", domain], (old: any) => {
        const updated = {
          ...old,
          monitors: old.monitors.map(
            (monitor: APIMonitoringEnrollmentMonitorConfiguration) =>
              monitor.type !== monitorType
                ? monitor
                : { ...monitor, is_enabled: !isEnabled }
          ),
        };

        return updated;
      });

      return { previous };
    },
    onSuccess: async (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["getMonitor", domain],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["monitor portfolio", "monitors", "list"],
        refetchType: "all",
      });
    },
    onError: (err, variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["getMonitor", domain], context.previous);
      }
      showToast();
    },
  });

  return (
    <LoadingButton
      sx={{ pb: 0, pt: 0 }}
      size="small"
      loading={configurationMutation.isPending}
      onClick={() => configurationMutation.mutate()}
      variant="outlined"
    >
      {isEnabled ? "Pause" : "Resume"}
    </LoadingButton>
  );
}
